import { Alert, Flex, Skeleton } from '@backstage/ui';
import { useRef, useState } from 'react';

import { CategoriesTable } from './CategoriesTable';
import { KpiRow } from './KpiRow';
import { ProjectsTable } from './ProjectsTable';
import { Separator } from 'react-aria-components';
import { TeamEmptyState } from './TeamEmptyState';

import { useTeamScorecard } from '../../hooks/useTeamScorecard';

export const EntityTeamScorecardsTab = () => {
  const { loading, error, team, summary } = useTeamScorecard();
  const [categoryFilters, setCategoryFilters] = useState<Set<string>>(
    new Set(),
  );
  const projectsRef = useRef<HTMLDivElement>(null);

  if (loading) {
    return (
      <Flex direction="column" gap="6" p="4" aria-busy="true">
        <Flex gap="4">
          {[0, 1, 2].map(i => (
            <Skeleton key={i} width="100%" height={120} />
          ))}
        </Flex>
        <Skeleton width="100%" height={280} />
        <Skeleton width="100%" height={360} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" p="4">
        <Alert
          status="danger"
          icon
          title={`Failed to load scorecards: ${error.message}`}
        />
      </Flex>
    );
  }

  if (!team || !summary || summary.projects.length === 0) {
    return (
      <Flex direction="column" p="4">
        <TeamEmptyState team={team} />
      </Flex>
    );
  }

  const toggleCategory = (id: string) => {
    const isAdding = !categoryFilters.has(id);
    setCategoryFilters(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    if (isAdding) {
      projectsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const categoryFilterItems = summary.categories
    .filter(c => c.id !== null && categoryFilters.has(c.id!))
    .map(c => ({ id: c.id!, name: c.name }));

  return (
    <Flex direction="column" gap="6" p="4">
      <KpiRow categories={summary.categories} projects={summary.projects} />
      <Separator />
      <CategoriesTable
        categories={summary.categories}
        projectsTotal={summary.projects.length}
        selectedCategoryIds={categoryFilters}
        onCategoryClick={toggleCategory}
      />
      <Separator />
      <div ref={projectsRef}>
        <ProjectsTable
          projects={summary.projects}
          categoryFilters={categoryFilters}
          categoryFilterItems={categoryFilterItems}
          onRemoveCategoryFilter={id => {
            setCategoryFilters(prev => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }}
          onClearAllCategoryFilters={() => setCategoryFilters(new Set())}
        />
      </div>
    </Flex>
  );
};
